import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Download, Mail, Edit2, Save, X, Trash2 } from 'lucide-react';
import api from '../api';

const fmtCurrency = (v) => v != null ? `$${Number(v).toFixed(2)}` : '$0.00';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

export default function SalesReturnDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ret, setRet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editRemarks, setEditRemarks] = useState('');
  const [editDate, setEditDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [emailAddr, setEmailAddr] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.request(`/api/sales-returns/${id}`);
        setRet(data);
        setEditRemarks(data.remarks || '');
        setEditDate(data.return_date ? data.return_date.split('T')[0] : '');
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.request(`/api/sales-returns/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ remarks: editRemarks, return_date: editDate }),
      });
      setRet(updated);
      setEditing(false);
    } catch (err) { alert('Failed to save: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDownloadPdf = async () => {
    try {
      await api.downloadReportExport(`/api/sales-returns/${id}/pdf`);
    } catch (err) { alert('PDF download failed: ' + err.message); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete credit memo ${ret.return_number}? This will reverse all inventory changes and restore the original sales order.`)) return;
    try {
      await api.request(`/api/sales-returns/${id}`, { method: 'DELETE' });
      navigate('/sales-returns');
    } catch (err) { alert('Delete failed: ' + err.message); }
  };

  const handleEmail = async () => {
    if (!emailAddr) { alert('Enter an email address'); return; }
    setEmailing(true);
    try {
      await api.request(`/api/sales-returns/${id}/email`, {
        method: 'POST',
        body: JSON.stringify({ email: emailAddr, message: emailMsg }),
      });
      alert('Credit memo emailed successfully!');
      setShowEmail(false);
      setEmailAddr('');
      setEmailMsg('');
    } catch (err) { alert('Email failed: ' + err.message); }
    finally { setEmailing(false); }
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (!ret) return <div className="p-8 text-center text-red-500">Return not found</div>;

  return (
    <div data-testid="sales-return-detail-page">
      {/* Controls - hidden when printing */}
      <div className="print:hidden bg-card border-b px-6 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-muted" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="text-sm text-muted-foreground flex-1">Credit Memo — {ret.return_number}</span>
        <button onClick={() => setEditing(!editing)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-muted" data-testid="edit-return-btn">
          <Edit2 className="h-3.5 w-3.5" /> Edit
        </button>
        <button onClick={handleDownloadPdf} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-muted" data-testid="download-pdf-btn">
          <Download className="h-3.5 w-3.5" /> PDF
        </button>
        <button onClick={() => setShowEmail(!showEmail)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-muted" data-testid="email-credit-btn">
          <Mail className="h-3.5 w-3.5" /> Email
        </button>
        <button onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90"
          data-testid="print-credit-btn">
          <Printer className="h-4 w-4" /> Print
        </button>
        <button onClick={handleDelete}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-md hover:bg-red-50"
          data-testid="delete-return-btn">
          <Trash2 className="h-3.5 w-3.5" /> Delete
        </button>
      </div>

      {/* Email modal */}
      {showEmail && (
        <div className="print:hidden bg-card border-b px-6 py-4 space-y-3">
          <div className="flex gap-3 items-end max-w-xl">
            <div className="flex-1">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Email To *</label>
              <input type="email" value={emailAddr} onChange={(e) => setEmailAddr(e.target.value)}
                placeholder="customer@example.com" className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="email-addr-input" />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Message (optional)</label>
              <input type="text" value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)}
                placeholder="Additional message..." className="w-full px-3 py-2 border border-input rounded-md text-sm" />
            </div>
            <button onClick={handleEmail} disabled={emailing}
              className="px-4 py-2 bg-primary text-white rounded-md text-sm hover:bg-primary/90 disabled:opacity-50" data-testid="send-email-btn">
              {emailing ? 'Sending...' : 'Send'}
            </button>
            <button onClick={() => setShowEmail(false)} className="px-3 py-2 border rounded-md text-sm hover:bg-muted">Cancel</button>
          </div>
        </div>
      )}

      {/* Edit form */}
      {editing && (
        <div className="print:hidden bg-card border-b px-6 py-4 space-y-3">
          <div className="flex gap-3 items-end max-w-xl">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Return Date</label>
              <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)}
                className="px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-date" />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Remarks</label>
              <input type="text" value={editRemarks} onChange={(e) => setEditRemarks(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-remarks" />
            </div>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-md text-sm disabled:opacity-50" data-testid="save-edit-btn">
              <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-2 border rounded-md text-sm hover:bg-muted">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Printable Credit Memo */}
      <div className="max-w-[800px] mx-auto bg-white p-8 my-6 print:my-0 print:p-6 print:shadow-none shadow-lg" style={{color: '#111'}}>
        <div className="flex justify-between items-start border-b-2 border-gray-800 pb-4 mb-6">
          <div className="flex items-start gap-3">
            <img src="/logo.jpg" alt="Webb Supply" className="h-14 w-14 rounded-md object-cover" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight" style={{color: '#111'}}>Webb Supply</h1>
              <div className="text-sm mt-1" style={{color: '#444'}}>
                <div>227 County Road 125 East</div>
                <div>Norris City, IL 62869</div>
                <div>618-962-3211</div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <h2 className="text-3xl font-bold tracking-tight text-red-800">CREDIT MEMO</h2>
            <div className="text-sm mt-2" style={{color: '#444'}}>
              <div><strong>Credit #:</strong> {ret.return_number}</div>
              <div><strong>Date:</strong> {fmtDate(ret.return_date || ret.created_at)}</div>
              <div><strong>Orig. SO #:</strong> {ret.sales_order_number}</div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-sm font-bold uppercase text-gray-600 mb-1">Credit To</h3>
          <p className="text-base font-medium">{ret.customer_name}</p>
        </div>

        <table className="w-full text-sm mb-6" style={{borderCollapse: 'collapse'}}>
          <thead>
            <tr style={{backgroundColor: '#8b0000', color: 'white'}}>
              <th className="text-left px-3 py-2 font-medium">Item</th>
              <th className="text-left px-3 py-2 font-medium">SKU</th>
              <th className="text-right px-3 py-2 font-medium">Qty</th>
              <th className="text-right px-3 py-2 font-medium">Price</th>
              <th className="text-center px-3 py-2 font-medium">Tax</th>
              <th className="text-right px-3 py-2 font-medium">Credit</th>
            </tr>
          </thead>
          <tbody>
            {(ret.line_items || []).map((item, i) => (
              <tr key={i} style={{borderBottom: '1px solid #e0e0e0', backgroundColor: i % 2 === 0 ? '#fff' : '#f8f8f8'}}>
                <td className="px-3 py-2">{item.variant_name}</td>
                <td className="px-3 py-2 text-gray-500">{item.sku}</td>
                <td className="px-3 py-2 text-right">{item.quantity}</td>
                <td className="px-3 py-2 text-right">{fmtCurrency(item.unit_price)}</td>
                <td className="px-3 py-2 text-center">{item.is_taxable ? 'Y' : 'N'}</td>
                <td className="px-3 py-2 text-right text-red-700">-{fmtCurrency(item.subtotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className="w-64">
            <div className="flex justify-between py-1 text-sm">
              <span>Credit Subtotal:</span>
              <span className="text-red-700">-{fmtCurrency(ret.subtotal)}</span>
            </div>
            {ret.tax > 0 && (
              <div className="flex justify-between py-1 text-sm">
                <span>Tax Credit:</span>
                <span className="text-red-700">-{fmtCurrency(ret.tax)}</span>
              </div>
            )}
            <div className="flex justify-between py-2 text-lg font-bold border-t-2 border-red-800 mt-1">
              <span>TOTAL CREDIT:</span>
              <span className="text-red-800">-{fmtCurrency(ret.total)}</span>
            </div>
          </div>
        </div>

        {ret.remarks && (
          <div className="mt-6 pt-4 border-t">
            <h3 className="text-sm font-bold text-gray-600 mb-1">Notes</h3>
            <p className="text-sm text-gray-600">{ret.remarks}</p>
          </div>
        )}

        <div className="mt-8 text-center text-xs text-gray-400">
          <p>This credit memo has been applied to your account.</p>
          <p className="mt-1">Webb Supply - 227 County Road 125 East, Norris City, IL 62869 - 618-962-3211</p>
        </div>
      </div>
    </div>
  );
}
